/**
 * `neutral` — a deliberately plain second theme that exists only to prove the
 * shell is brand-neutral: a different accent hue (blue with white text), cool
 * greys, a system font stack with no web-font stylesheet, rounder corners, a
 * smaller body size and sentence-case labels. Select it with `VITE_THEME=neutral`
 * or `/api/config.theme = "neutral"`. It is throwaway and carries no brand.
 */
import { defineTheme } from "../types.js";

const blue = {
  "100": "#DBEAFE",
  "200": "#93C5FD",
  "300": "#60A5FA",
  "400": "#3B82F6",
  "500": "#2563EB",
  "600": "#1D4ED8",
  "700": "#1E40AF",
} as const;

const cool = {
  "0": "#FFFFFF",
  "50": "#F5F7FA",
  "100": "#EEF1F5",
  "200": "#E2E8F0",
  "300": "#CBD5E1",
  "400": "#94A3B8",
  "500": "#475569",
  "600": "#334155",
  "700": "#2C3A52",
  "800": "#1F2B40",
  "850": "#182338",
  "900": "#111A2B",
  "950": "#0E1524",
  "1000": "#0B1220",
} as const;

// Inline SVG (text, not a binary): a rounded square with a "P" — enough to show
// the logo slot is theme-driven without shipping image files.
const markSvg =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="${blue["500"]}"/><text x="32" y="43" font-family="system-ui, sans-serif" font-size="34" font-weight="700" fill="#FFFFFF" text-anchor="middle">P</text></svg>`,
  );

export const neutral = defineTheme({
  name: "neutral",
  productName: "Portal",
  shortName: "Portal",
  legalName: "Portal (neutral test theme)",
  logo: {
    lockupDark: null,
    lockupLight: null,
    mark: markSvg,
    favicon: markSvg,
    themeColor: blue["500"],
  },
  palette: {
    primary: blue,
    neutral: cool,
  },
  fonts: {
    display: { family: null, stack: '"Segoe UI", system-ui, -apple-system, Roboto, Helvetica, Arial, sans-serif' },
    body: { family: null, stack: '"Segoe UI", system-ui, -apple-system, Roboto, Helvetica, Arial, sans-serif' },
    mono: { family: null, stack: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace' },
    stylesheetHref: null,
  },
  type: {
    body: "14px",
    xs: "11px",
    sm: "12px",
    md: "14px",
    lg: "16px",
    xl: "20px",
    "2xl": "24px",
    "3xl": "30px",
    display: "40px",
    leadingBody: "1.5",
    leadingTight: "1.2",
    trackingEyebrow: "0.02em",
    eyebrowTransform: "none",
  },
  radius: {
    sm: "6px",
    md: "8px",
    lg: "12px",
    pill: "999px",
  },
  spacing: {
    "1": "4px",
    "2": "8px",
    "3": "12px",
    "4": "16px",
    "5": "20px",
    "6": "24px",
    "7": "32px",
    "8": "40px",
    "9": "48px",
    "10": "64px",
  },
  modes: {
    light: {
      colorScheme: "light",
      bg: cool["50"],
      surface: cool["0"],
      surfaceRaised: cool["0"],
      surfaceSunken: cool["100"],
      border: cool["200"],
      borderStrong: cool["300"],
      text: cool["1000"],
      textSecondary: cool["600"],
      textMuted: cool["500"],
      textFaint: "#8490A0", // Derived: also keeps input boundaries above 3:1 on white.
      accent: blue["500"],
      accentHover: blue["600"],
      accentFg: cool["0"],
      accentSoft: "rgba(37, 99, 235, 0.10)",
      accentBorder: "rgba(37, 99, 235, 0.45)",
      accentFigure: blue["600"],
      eyebrow: cool["500"],
      link: blue["600"],
      linkHover: blue["700"],
      focusRing: "rgba(37, 99, 235, 0.4)",
      rowHover: "rgba(15, 23, 42, 0.04)",
      overlay: "rgba(15, 23, 42, 0.5)",
      semantic: {
        success: { fg: "#15803D", bg: "rgba(21, 128, 61, 0.10)", border: "rgba(21, 128, 61, 0.35)" },
        warning: { fg: "#B45309", bg: "rgba(180, 83, 9, 0.10)", border: "rgba(180, 83, 9, 0.35)" },
        danger: { fg: "#B91C1C", bg: "rgba(185, 28, 28, 0.08)", border: "rgba(185, 28, 28, 0.35)" },
        info: { fg: "#1D4ED8", bg: "rgba(29, 78, 216, 0.10)", border: "rgba(29, 78, 216, 0.35)" },
      },
      shadowSm: "0 1px 2px rgba(15, 23, 42, 0.06)",
      shadowMd: "0 10px 30px rgba(15, 23, 42, 0.15)",
    },
    dark: {
      colorScheme: "dark",
      bg: cool["1000"],
      surface: cool["900"],
      surfaceRaised: cool["850"],
      surfaceSunken: cool["950"],
      border: cool["800"],
      borderStrong: cool["700"],
      text: "#E5EAF2",
      textSecondary: "#B8C2D3",
      textMuted: "#8B98AE",
      textFaint: "#5B6B84",
      accent: blue["300"],
      accentHover: blue["200"],
      accentFg: cool["1000"],
      accentSoft: "rgba(96, 165, 250, 0.14)",
      accentBorder: "rgba(96, 165, 250, 0.45)",
      accentFigure: blue["200"],
      eyebrow: "#8B98AE",
      link: blue["200"],
      linkHover: blue["100"],
      focusRing: "rgba(147, 197, 253, 0.4)",
      rowHover: "rgba(229, 234, 242, 0.05)",
      overlay: "rgba(0, 0, 0, 0.6)",
      semantic: {
        success: { fg: "#4ADE80", bg: "rgba(74, 222, 128, 0.12)", border: "rgba(74, 222, 128, 0.35)" },
        warning: { fg: "#FBBF24", bg: "rgba(251, 191, 36, 0.12)", border: "rgba(251, 191, 36, 0.35)" },
        danger: { fg: "#F87171", bg: "rgba(248, 113, 113, 0.12)", border: "rgba(248, 113, 113, 0.35)" },
        info: { fg: "#93C5FD", bg: "rgba(147, 197, 253, 0.12)", border: "rgba(147, 197, 253, 0.35)" },
      },
      shadowSm: "none",
      shadowMd: "0 10px 30px rgba(0, 0, 0, 0.5)",
    },
  },
  email: {
    senderDisplayName: "Portal",
  },
});
